import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  reportUnintendedReceipient,
  sendCorrectionRequest,
} from "../../apiServices";
import {
  TERMS_AND_CONDITIONS_URL,
  PRIVACY_POLICY_URL,
  CIGNA_URL,
} from "../../constants";
import "./ErrorReportForm.scss";
import CignaLogo from "../CignaLogo/CignaLogo.js";

const ErrorReportForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reviewEmailId } = useParams();
  const [relationship, setRelationship] = useState();
  const [loader, setLoader] = useState(false);
  const [subscriberFirstName, setSubscriberFirstName] = useState();
  const [patientFirstName, setPatientFirstName] = useState();
  const [patientAge, setPatientAge] = useState();
  const [crmProviderNameBody, setCrmProviderNameBody] = useState();
  const [practiceAddress, setPracticeAddress] = useState();

  const [selectedOption, setSelectedOption] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [selectedErrorType, setSelectedErrorType] = useState("");

  const handleOptionChange = (event) => {
    setSelectedErrorType(event.target.id);
    setSelectedOption(event.target.value);
  };

  const handleOtherReasonChange = (event) => {
    setOtherReason(event.target.value);
  };

  // Test URL Check
  // const testUrlOfCorrection =
  //   "https://reviews.cigna.com/reviews/pcv2/correction/3621b76d-43d8-4769-a6b3-ab01d6564a37?EID=EM_06_616&relationship=patient&Lead_Key_ID=231845202049";

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setRelationship(searchParams.get("relationship") || relationship);
  }, [
    location.pathname,
    location.search,
    navigate,
    relationship,
    reviewEmailId,
  ]);
  useEffect(() => {
    if (!relationship || !reviewEmailId) return;
    const performCorrectionFetch = async () => {
      try {
        setLoader(true);
        const response = await sendCorrectionRequest({
          relationship,
          reviewEmailId,
        });
        if (response?.data?.isReviewComplete) {
          navigate("/review-completed");
        } else {
          setSubscriberFirstName(response?.data?.subscriberFirstName);
          setPatientFirstName(response?.data?.patientFirstName);
          setCrmProviderNameBody(response?.data?.crmProviderNameBody);
          setPracticeAddress(response?.data?.practiceAddress);
          setPatientAge(response?.data?.patientAge);
        }
      } catch (error) {
        navigate("/error");
        return error;
      }
      setLoader(false);
    };

    performCorrectionFetch();
  }, [navigate, relationship, reviewEmailId]);

  const isSubmitDisabled =
    !selectedOption || (selectedOption === "other" && !otherReason.trim());
  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        reviewEmailId,
        relationship,
        errorType: selectedErrorType,
        errorReason:
          selectedOption === "other" ? otherReason.trim() : selectedOption,
      };

      await reportUnintendedReceipient(payload);
      navigate("/completed");
    } catch (error) {
      navigate("/error");
      return error;
    }
  };

  if (loader) {
    return null;
  }

  return (
    <div className="container-fluid custom-container error-report-content">
      <div className="container main-layout">
        <div className="card">
          <div className="logo-header d-flex align-items-center logo-section">
          <CignaLogo className="h-14 w-auto mb-6" />
          </div>
          <div className="container main-content">
            <header>
              {relationship === "subscriber" ? (
                <>
                  <p className="email_text">
                    This email was intended for {subscriberFirstName}
                  </p>
                  <p className="email_text_sub">
                    based on {subscriberFirstName}'s recent visit with{" "}
                    {crmProviderNameBody} at {practiceAddress}
                  </p>
                </>
              ) : (
                <>
                  <p className="email_text">
                    This email was intended for{" "}
                    {patientAge < 18 ? subscriberFirstName : patientFirstName}
                  </p>
                  <p className="email_text_sub">
                    based on{" "}
                    {patientAge < 18 ? subscriberFirstName : patientFirstName}'s
                    recent visit with {crmProviderNameBody} at {practiceAddress}
                  </p>
                </>
              )}
            </header>

            <main>
              <form onSubmit={handleSubmit}>
                <section className="message">
                  <h6>Please describe the nature of this error:</h6>
                  <ul className="options">
                    <li className="option">
                      <span className="radio-wrapper">
                        <input
                          type="radio"
                          name="option"
                          id="incorrect_contact"
                          value="I am not the intended contact person"
                          checked={
                            selectedOption ===
                            "I am not the intended contact person"
                          }
                          onChange={handleOptionChange}
                        />
                      </span>
                      <label
                        className="radio-label"
                        htmlFor="incorrect_contact"
                      >
                        I am not the intended contact person
                      </label>
                    </li>
                    <li className="option">
                      <span className="radio-wrapper">
                        <input
                          type="radio"
                          name="option"
                          id="incorrect_provider"
                          value="The intended contact is correct, but the healthcare provider information is inaccurate"
                          checked={
                            selectedOption ===
                            "The intended contact is correct, but the healthcare provider information is inaccurate"
                          }
                          onChange={handleOptionChange}
                        />
                      </span>
                      <label
                        className="radio-label"
                        htmlFor="incorrect_provider"
                      >
                        The intended contact is correct, but the healthcare
                        provider information is inaccurate
                      </label>
                    </li>
                    <li className="option">
                      <span className="radio-wrapper">
                        <input
                          type="radio"
                          name="option"
                          id="incorrect_patient"
                          value={`The intended contact and provider are correct but ${subscriberFirstName} did not attend this visit`}
                          checked={
                            selectedOption ===
                            `The intended contact and provider are correct but ${subscriberFirstName} did not attend this visit`
                          }
                          onChange={handleOptionChange}
                        />
                      </span>
                      <label
                        className="radio-label"
                        htmlFor="incorrect_patient"
                      >
                        The intended contact and provider are correct but{" "}
                        {subscriberFirstName} did not attend this visit
                      </label>
                    </li>
                    <li className="option">
                      <span className="radio-wrapper">
                        <input
                          type="radio"
                          name="option"
                          id="staff_only"
                          value="The above information is correct, but only office and/or facility staff were seen during the visit (e.g. nurses, physician's assistants, technicians)"
                          checked={
                            selectedOption ===
                            "The above information is correct, but only office and/or facility staff were seen during the visit (e.g. nurses, physician's assistants, technicians)"
                          }
                          onChange={handleOptionChange}
                        />
                      </span>
                      <label className="radio-label" htmlFor="staff_only">
                        The above information is correct, but only office and/or
                        facility staff were seen during the visit (e.g. nurses,
                        physician's assistants, technicians)
                      </label>
                    </li>
                    <li className="option">
                      <span className="radio-wrapper">
                        <input
                          type="radio"
                          name="option"
                          id="false_error"
                          value="I have not experienced an error, all of the above information is correct"
                          checked={
                            selectedOption ===
                            "I have not experienced an error, all of the above information is correct"
                          }
                          onChange={handleOptionChange}
                        />
                      </span>
                      <label className="radio-label" htmlFor="false_error">
                        I have not experienced an error, all of the above
                        information is correct
                      </label>
                    </li>
                    <li className="option">
                      <span className="radio-wrapper">
                        <input
                          type="radio"
                          name="option"
                          id="other"
                          value="other"
                          checked={selectedOption === "other"}
                          onChange={handleOptionChange}
                        />
                      </span>
                      <label className="radio-label" htmlFor="other">
                        Other
                      </label>
                      <input
                        type="text"
                        className="other-textfield"
                        value={otherReason}
                        onChange={handleOtherReasonChange}
                        disabled={selectedOption !== "other"}
                      />
                    </li>
                  </ul>
                  <button
                    type="submit"
                    className={`submitButton btn ${
                      !isSubmitDisabled ? "btn-primary" : "btn-secondary"
                    } custom-margin custom-width`}
                    disabled={isSubmitDisabled}
                  >
                    Submit
                  </button>
                </section>
              </form>
            </main>
          </div>
        </div>
      </div>

      <div className="footer-warpper">
        <div className="container footer">
          <p className="footerParaText">
            Your review will be displayed on myCigna for other patients to see
            when choosing a healthcare provider. While your name will not be
            included, basic demographic information (such as age range and
            gender) may be.
          </p>
          <p className="secondParaText">
            This does not supplement or replace Cigna's current complaint and
            quality monitoring process. For any specific issues or concerns that
            you would like to share with Cigna for further review, please call
            1.800.Cigna24 or 1.800.244.6224.
          </p>
          <div className="reviewLinks mb-2">
            <a
              href={CIGNA_URL}
              target="_blank"
              rel="noreferrer"
              className="mx-2 text-primary"
            >
              Cigna
            </a>
            <span>|</span>
            <a
              href={TERMS_AND_CONDITIONS_URL}
              target="_blank"
              rel="noreferrer"
              className="mx-2"
            >
              Terms and Conditions
            </a>
            <span>|</span>
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noreferrer"
              className="mx-2"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorReportForm;
